import React, { useContext, useEffect, useState } from "react";
import { Form, useForm, useFormState } from "react-final-form";
import arrayMutators from "final-form-arrays";
import { RadixSelect, Textarea, TextField } from "components/Form/Field";
import { IntlContext, AuthContext } from "context";
import { Category } from "types/category";
import { Options } from "ui/Form/Element/Select";
import {
    fetchServiceAvgPrice,
    useCreateService,
    useDeleteService,
    useUpdateService,
} from "hooks/service";
import { ServiceInterface } from "types/service";
import { Button } from "components/ui/button";
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from "components/ui/dialog";
import formatErrorResponse from "helpers/form/formatErrorResponse";
import { useNavigate } from "react-router";
import { Link } from "react-router-dom";
import { ROUTES } from "routes";
import { profileFieldGroupProps, profileLabelProps } from "./formStyles";
import { Info } from "lucide-react";
import { toast } from "sonner";
import { getActiveServiceCategoryOptions } from "helpers/category/getActiveServiceCategoryOptions";
import { renderCategoryFields } from "app/Profile/common/CategoryFields/renderCategoryFields";

function normalizeServiceData(data: unknown): Record<string, unknown> {
    const d: Record<string, unknown> = !data || Array.isArray(data) ? {} : (data as Record<string, unknown>);
    return {
        ...d,
        bring_products: d.bring_products !== undefined && d.bring_products !== null
            ? String(Number(d.bring_products))
            : "0",
        bring_tools: d.bring_tools !== undefined && d.bring_tools !== null
            ? String(Number(d.bring_tools))
            : "0",
    };
}

export enum Mode {
    ADD = "ADD",
    VIEW = "VIEW",
    EDIT = "EDIT",
}

interface ServiceFormProps {
    mode?: Mode;
}

const ServiceForm: React.FC<ServiceFormProps> = ({ mode }) => {
    const intl = useContext(IntlContext);
    const { account } = useContext(AuthContext);
    const navigate = useNavigate();
    const deleteServiceMutation = useDeleteService();

    const providerServices = account?.resources?.provider?.[0]?.provider_services;

    const subcateg = [...new Set(providerServices?.map((item) => item.subcategory as string) ?? [])];

    const form = useForm();
    const { values, submitting } = useFormState();
    const [pastCategory, setPastCategory] = useState<string>(values.category);
    const [avgPrice, setAvgPrice] = useState<string>("");
    const categoriesWithoutSub = [Category.CHILDCARE.toString()];

    const [showDeleteModal, setShowDeleteModal] = useState(false);

    const handleOnClickDeleteService = async () => {
        try {
            await deleteServiceMutation.mutateAsync(values.id);
            setShowDeleteModal(false);
            navigate(ROUTES.MY_SERVICES);
            toast.success(intl.formatMessage("service.message.delete_success"));
        } catch {
            toast.error(intl.formatMessage("service.message.delete_error"));
        }
    };

    const categories = getActiveServiceCategoryOptions(intl);

    useEffect(() => {
        setAvgPrice("");
        if (!values.category) {
            form.change("subcategory", undefined);
        } else if (values.category !== pastCategory) {
            form.change("subcategory", undefined);
            setPastCategory(values.category);
            fetchServiceAvgPrice({ filter_is_active: 1, category: values.category }).then((data) => {
                if (data) {
                    setAvgPrice(parseFloat(data).toFixed(2).replace(".", ","));
                }
            });
        }
    }, [values.category, form, pastCategory]);

    return (
        <div className="space-y-6">
            {mode === Mode.ADD && (
                <div className="grid gap-6 sm:grid-cols-2">
                    <RadixSelect
                        inputProps={{
                            name: "category",
                            placeholder: intl.formatMessage("service.placeholder.categories"),
                            required: true,
                            options: categories,
                        }}
                        labelProps={profileLabelProps(intl.formatMessage("service.label.categories"), true)}
                        fieldGroupProps={profileFieldGroupProps()}
                    />

                    {values?.category && categoriesWithoutSub.indexOf(values.category) === -1 && (
                        <RadixSelect
                            inputProps={{
                                name: "subcategory",
                                placeholder: intl.formatMessage("service.placeholder.subcategories"),
                                required: true,
                                disabled: values.category === undefined,
                                options: values?.category
                                    ? Object.entries(
                                          Category[values.category as keyof typeof Category].values
                                      ).map(([_key, value]) => {
                                          const stringValue = value.toString();
                                          return {
                                              label: intl.formatMessage(
                                                  `service.label.subcategory.${stringValue}`
                                              ),
                                              value: stringValue,
                                              disabled: subcateg.includes(stringValue),
                                          } as Options;
                                      })
                                    : [],
                            }}
                            labelProps={profileLabelProps(intl.formatMessage("service.label.subcategories"), true)}
                            fieldGroupProps={profileFieldGroupProps()}
                        />
                    )}
                </div>
            )}

            {avgPrice && (
                <p className="flex items-start gap-2 text-sm italic text-slate-500">
                    <Info className="mt-0.5 h-4 w-4 shrink-0 text-slate-400" strokeWidth={2} />
                    <span>
                        Le tarif horaire moyen de cette catégorie est de{" "}
                        <span className="font-medium text-primary">{avgPrice}€</span>
                    </span>
                </p>
            )}

            {renderCategoryFields(values?.subcategory, {
                disabled: mode === Mode.VIEW,
                isAd: false,
            })}

            <TextField
                inputProps={{
                    name: "price",
                    required: true,
                    disabled: mode === Mode.VIEW,
                    min: 0,
                    placeholder: intl.formatMessage("service.placeholder.price"),
                }}
                labelProps={profileLabelProps(intl.formatMessage("service.label.price"), true)}
                fieldGroupProps={profileFieldGroupProps()}
                symbol="€"
            />

            {mode !== Mode.VIEW && (
                <p className="flex items-start gap-2 text-sm italic text-slate-500">
                    <Info className="mt-0.5 h-4 w-4 shrink-0 text-slate-400" strokeWidth={2} />
                    <span>
                        Consultez les salaires horaires minimums{" "}
                        <Link
                            className="font-medium text-primary hover:underline"
                            to="https://www.cesu.urssaf.fr/info/accueil/salaires-minimums.html"
                            target="_blank"
                            rel="noopener noreferrer"
                        >
                            ici
                        </Link>
                    </span>
                </p>
            )}

            <Textarea
                inputProps={{
                    name: "description",
                    placeholder: intl.formatMessage("service.label.description.placeholder"),
                    disabled: mode === Mode.VIEW,
                    required: true,
                }}
                labelProps={profileLabelProps(intl.formatMessage("service.label.description"), true)}
                fieldGroupProps={profileFieldGroupProps()}
            />

            {mode !== Mode.VIEW && (
                <>
                    <div
                        className={`mt-8 flex flex-col-reverse gap-3 border-t border-gray-100 pt-6 sm:flex-row sm:items-center ${
                            mode === Mode.EDIT ? "sm:justify-between" : "sm:justify-end"
                        }`}
                    >
                        {mode === Mode.EDIT && (
                            <Button
                                type="button"
                                variant="outline"
                                className="rounded-xl text-destructive border-destructive hover:bg-destructive/10 hover:text-destructive"
                                onClick={() => setShowDeleteModal(true)}
                            >
                                {intl.formatMessage("service.label.delete")}
                            </Button>
                        )}
                        <Button type="submit" className="rounded-xl sm:min-w-[220px]" disabled={submitting}>
                            {mode === Mode.EDIT
                                ? intl.formatMessage("service.label.edit.validate")
                                : intl.formatMessage("service.label.publish")}
                        </Button>
                    </div>

                    {mode === Mode.EDIT && (
                        <Dialog open={showDeleteModal} onOpenChange={setShowDeleteModal}>
                            <DialogContent className="sm:max-w-md">
                                <DialogHeader>
                                    <DialogTitle>{intl.formatMessage("service.label.modal.title")}</DialogTitle>
                                    <DialogDescription>
                                        {intl.formatMessage("service.label.modal.message")}
                                    </DialogDescription>
                                </DialogHeader>
                                <DialogFooter className="gap-2 sm:gap-0">
                                    <Button variant="outline" onClick={() => setShowDeleteModal(false)}>
                                        {intl.formatMessage("generic.action.cancel")}
                                    </Button>
                                    <Button
                                        variant="destructive"
                                        disabled={deleteServiceMutation.isPending}
                                        onClick={() => void handleOnClickDeleteService()}
                                    >
                                        {intl.formatMessage("generic.action.confirm")}
                                    </Button>
                                </DialogFooter>
                            </DialogContent>
                        </Dialog>
                    )}
                </>
            )}
        </div>
    );
};

interface ServiceFormWrapperProps {
    initialValues?: ServiceInterface;
    mode?: Mode;
}

const EMPTY_SERVICE_INITIAL_VALUES = {} as ServiceInterface;

const ServiceFormWrapper: React.FC<ServiceFormWrapperProps> = ({
    initialValues = EMPTY_SERVICE_INITIAL_VALUES,
    mode = Mode.EDIT,
}) => {
    const intl = useContext(IntlContext);
    const navigate = useNavigate();
    const createServiceMutation = useCreateService();
    const updateServiceMutation = useUpdateService();

    const formInitialValues = React.useMemo(
        () => ({
            ...initialValues,
            data: normalizeServiceData((initialValues as ServiceInterface | undefined)?.data),
        }),
        [initialValues]
    );

    const isRecord = (value: unknown): value is Record<string, unknown> =>
        typeof value === "object" && value !== null;

    const onSubmit = async (values: ServiceInterface) => {
        try {
            if (initialValues?.id) {
                await updateServiceMutation.mutateAsync({ id: initialValues.id, data: values });
                toast.success(intl.formatMessage("service.message.update_success"));
            } else {
                await createServiceMutation.mutateAsync(values);
                toast.success(intl.formatMessage("service.message.create_success"));
            }

            navigate(ROUTES.MY_SERVICES);
        } catch (err: unknown) {
            if (isRecord(err) && "response" in err && isRecord(err.response) && "data" in err.response) {
                const responseData = err.response.data;
                if (isRecord(responseData) && "data" in responseData) {
                    return formatErrorResponse(responseData as { data: unknown });
                }
            }
            toast.error(intl.formatMessage("service.message.update_error"));
            throw err;
        }
    };

    return (
        <Form
            onSubmit={onSubmit}
            initialValues={formInitialValues}
            mutators={{ ...arrayMutators }}
            render={({ handleSubmit }) => (
                <form onSubmit={handleSubmit}>
                    <ServiceForm mode={mode} />
                </form>
            )}
        />
    );
};

export default ServiceFormWrapper;
